import { useState } from 'react'
import { useTranslations } from 'use-intl'
import { Button } from '@heroui/react'
import { ChevronDown, ChevronUp } from 'lucide-react'

interface MenuItem {
  title: string
  price?: string | null
  category?: string | null
  description?: string | null
}

interface ShopMenuSectionProps {
  menus: MenuItem[] | null
}

function MenuItemView({ item, marketPriceLabel }: { item: MenuItem; marketPriceLabel: string }) {
  return (
    <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-4 border-b border-outline-variant/10 pb-4 last:border-0 last:pb-0">
      <div className="flex-1">
        <h4 className="font-bold text-on-surface leading-tight mb-1">{item.title}</h4>
        {item.description && (
          <p className="text-xs text-on-surface-variant/80 font-['Noto_Sans_JP'] leading-relaxed">
            {item.description}
          </p>
        )}
      </div>
      <div className="shrink-0 flex items-start">
        <span className="font-bold text-sm tracking-wide whitespace-nowrap">
          {item.price ? item.price : marketPriceLabel}
        </span>
      </div>
    </div>
  )
}

export function ShopMenuSection({ menus }: ShopMenuSectionProps) {
  const t = useTranslations('shopDetail.menuSection')
  const [isExpanded, setIsExpanded] = useState(false)
  const marketPriceLabel = t('marketPrice', { defaultValue: 'Market price' })

  if (!menus || menus.length === 0) return null

  const INITIAL_LIMIT = 6
  const hasMore = menus.length > INITIAL_LIMIT
  const visibleItems = isExpanded ? menus : menus.slice(0, INITIAL_LIMIT)

  const renderContent = () => {
    if (!isExpanded) {
      return (
        <div className="flex flex-col gap-4">
          {visibleItems.map((item, idx) => (
            <MenuItemView key={idx} item={item} marketPriceLabel={marketPriceLabel} />
          ))}
        </div>
      )
    }

    // Grouped by category
    const grouped = visibleItems.reduce((acc, item) => {
      const cat = item.category || ''
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(item)
      return acc
    }, {} as Record<string, MenuItem[]>)

    return (
      <div className="flex flex-col gap-8">
        {Object.entries(grouped).map(([category, items], sectionIdx) => (
          <div key={category || `uncategorized-${sectionIdx}`}>
            {category && (
              <h4 className="text-sm font-bold text-primary mb-4 border-b border-outline-variant/20 pb-2">
                {category}
              </h4>
            )}
            <div className="flex flex-col gap-4">
              {items.map((item, idx) => (
                <MenuItemView key={idx} item={item} marketPriceLabel={marketPriceLabel} />
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <section id="menu" className="w-full pb-8 scroll-mt-24">
      <div className="bg-surface-container-lowest p-6 md:p-8 rounded-2xl border border-outline-variant/10 shadow-sm">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-[1px] w-12 bg-primary"></div>
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
            {t('title', { defaultValue: 'Menu' })}
          </h3>
        </div>

        <div className="mb-6">
          {renderContent()}
        </div>

        {hasMore && (
          <div className="flex justify-center mt-4">
            <Button
              variant="secondary"
              onPress={() => setIsExpanded(!isExpanded)}
              className="bg-surface-container-high text-on-surface font-medium px-6 py-2 rounded-full flex items-center gap-2 transition-all hover:bg-surface-container-highest"
            >
              {isExpanded ? (
                <>
                  {t('showLess', { defaultValue: 'Show less' })}
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  {t('showAll', { n: menus.length, defaultValue: `Show all ${menus.length} items` })}
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}
